import { useEffect, useState, useCallback } from "react";
import moment from "moment";
import { useAuth } from "@/hooks/use-auth";
import { getCallHistory } from "@/api/services";
import Header from "@/components/Header";
import Pagination from "@/components/Pagination";
import FilterBar from "@/components/Appointments/FilterBar";
import CallTable from "@/components/Calls/CallTable";
import CallGrid from "@/components/Calls/CallGrid";

const FILTERS = [
  "All Calls",
  "Completed",
  "Missed",
];
const DEFAULT_ITEMS_PER_PAGE = 10;

export default function CallsPage() {
  const { astrologer } = useAuth();
  const [calls, setCalls] = useState([]);
  const [activeFilter, setActiveFilter] = useState("All Calls");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
  const [viewMode, setViewMode] = useState("list");
  const [paginationInfo, setPaginationInfo] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [advancedFilters, setAdvancedFilters] = useState({
    sortBy: "newest",
    durations: "",
    startDate: undefined,
    endDate: undefined,
  });

  const badgeList = [
    { name: "All" },
    { name: "Completed" },
    { name: "Missed" },
    // { name: "Scheduled" },
  ];

  const fetchCalls = useCallback(async () => {
    if (!astrologer.astrologerId || !astrologer.accessToken) return;
    setIsLoading(true);
    setError(null);
    try {
      const filters = {
        page: currentPage,
        limit: itemsPerPage,
        ...advancedFilters,
      };
      if (activeFilter !== "All Calls") {
        if (activeFilter === "Completed") {
          filters.status = "completed";
        } else if (activeFilter === "Missed") {
          filters.status = "expired";
        }
      }
      if (searchTerm) {
        filters.searchTerm = searchTerm;
      }
      const { data } = await getCallHistory(
        astrologer.astrologerId,
        astrologer.accessToken,
        filters
      );
      const formattedCalls = data.appointments.map((d) => {
        const [hour, minute] = d.time.split(":").map(Number);
        console.log(d);
        return {
          id: d._id,
          name: d.user.name,
          date: moment(d.date).hour(hour).minute(minute).toDate(),
          status: d.status,
          image: d.user.picture,
          reference: d._id,
          duration: d.duration,
          mode: d.mode === "call" ? "Voice" : "Video",
          type: d.type,
          rate: astrologer?.personalInformation?.rate || 0,
        };
      });
      setCalls(formattedCalls);
      setPaginationInfo(data.pagination || {
        currentPage,
        totalPages: 1,
        totalItems: formattedCalls.length,
        hasNextPage: false,
        hasPrevPage: false,
      });
      setIsLoading(false);
    } catch (error) {
      setError(error.message || "Failed to fetch calls");
      setIsLoading(false);
    }
  }, [astrologer, activeFilter, searchTerm, currentPage, itemsPerPage, advancedFilters]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearch = (query) => {
    setSearchTerm(query);
  };

  const handleAdvancedFiltersChange = (filters) => {
    setAdvancedFilters(filters);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header name="Instant Consulations" />
      <div className="flex justify-center items-center w-full flex-col gap-6 px-6 mt-2 relative">
        <FilterBar
          filters={FILTERS}
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          viewMode={viewMode}
          setViewMode={setViewMode}
          onSearch={handleSearch}
          onAdvancedFiltersChange={handleAdvancedFiltersChange}
        />
        <div className="flex flex-col gap-4 mt-4 w-full">
          {isLoading ? (
            <div className="flex justify-center items-center w-full h-64">
              <span className="text-gray-400 text-lg">Loading...</span>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center w-full h-64">
              <span className="text-red-500 text-lg">{error}</span>
            </div>
          ) : (
            <>
              {/* Mobile View*/}
              <div className="block md:hidden w-full">
                <CallGrid paginatedData={calls} />
              </div>
              {/* Desktop View */}
              <div className="hidden md:block w-full">
                {viewMode === "grid" ? (
                  <CallGrid paginatedData={calls} />
                ) : (
                  <CallTable paginatedData={calls} />
                )}
              </div>
              <Pagination
                totalItems={paginationInfo.totalItems}
                itemsPerPage={itemsPerPage}
                currentPage={paginationInfo.currentPage}
                totalPages={paginationInfo.totalPages}
                onPageChange={handlePageChange}
                onItemsPerPageChange={setItemsPerPage}
                hasNextPage={paginationInfo.hasNextPage}
                hasPrevPage={paginationInfo.hasPrevPage}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}